import type { ObservationRecord } from '../types/observations';

export interface WearableInsightStats {
  avgRecovery?: number;
  avgSleepDuration?: number;
  avgSleepEfficiency?: number;
  avgRestingHr?: number;
  lowRecoveryDays14: number;
  poorSleepDays14: number;
  elevatedRestingHrDays14: number;
  totalObservations: number;
  recentObservationCount: number;
}

export interface PatientInterpretationCard {
  tone: 'stable' | 'watch' | 'action';
  title: string;
  summary: string;
  nextSteps: string[];
  caution: string;
}

export interface OpsReviewPromptSuggestion {
  key: string;
  title: string;
  detail: string;
  severity: 'routine' | 'priority' | 'urgent';
  shouldEscalate: boolean;
  escalationSummary?: string;
}

export interface EscalationLadderItem {
  key: string;
  label: string;
  level: 'monitor' | 'support' | 'escalate';
  detail: string;
}

export interface WearableInsightBundle {
  stats: WearableInsightStats;
  patientInterpretation: PatientInterpretationCard;
  reviewPrompts: OpsReviewPromptSuggestion[];
  escalationLadder: EscalationLadderItem[];
}

function toMillis(value?: string | null) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function round(value?: number, digits = 1) {
  if (value == null || Number.isNaN(value)) return undefined;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function average(values: number[]) {
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function normalizeObservation(observation: ObservationRecord) {
  return {
    display: String(observation.display || '').trim(),
    value: observation.valueNumber,
    unit: observation.unit || '',
    observedAt: observation.observedAt || null,
  };
}

export function buildWearableInsightBundle(observations: ObservationRecord[]): WearableInsightBundle {
  const now = Date.now();
  const last30Days = now - (30 * 24 * 60 * 60 * 1000);
  const last14Days = now - (14 * 24 * 60 * 60 * 1000);

  const normalized = observations
    .map(normalizeObservation)
    .filter((observation) => observation.value != null && !Number.isNaN(observation.value));

  const recent = normalized.filter((observation) => toMillis(observation.observedAt) >= last30Days);
  const recent14 = normalized.filter((observation) => toMillis(observation.observedAt) >= last14Days);

  const recoveryValues = recent
    .filter((observation) => observation.display.includes('Recovery score') && observation.unit === '%')
    .map((observation) => observation.value as number);
  const sleepDurationValues = recent
    .filter((observation) => observation.display.includes('Sleep duration') && observation.unit === 'h')
    .map((observation) => observation.value as number);
  const sleepEfficiencyValues = recent
    .filter((observation) => observation.display.includes('Sleep efficiency') && observation.unit === '%')
    .map((observation) => observation.value as number);
  const restingHrValues = recent
    .filter((observation) => {
      const label = observation.display.toLowerCase();
      return (
        (label.includes('resting heart rate') || label === 'heart rate' || label === 'resting hr')
        && observation.unit === 'beats/minute'
        && (observation.value as number) >= 45
        && (observation.value as number) <= 100
      );
    })
    .map((observation) => observation.value as number);

  const lowRecoveryDays14 = recent14.filter((observation) => observation.display.includes('Recovery score') && observation.unit === '%' && (observation.value as number) < 60).length;
  const poorSleepDays14 = recent14.filter((observation) => (
    (observation.display.includes('Sleep duration') && observation.unit === 'h' && (observation.value as number) < 6.5)
    || (observation.display.includes('Sleep efficiency') && observation.unit === '%' && (observation.value as number) < 85)
  )).length;
  const elevatedRestingHrDays14 = recent14.filter((observation) => {
    const label = observation.display.toLowerCase();
    return (
      (label.includes('resting heart rate') || label === 'heart rate' || label === 'resting hr')
      && observation.unit === 'beats/minute'
      && (observation.value as number) > 65
    );
  }).length;

  const stats: WearableInsightStats = {
    avgRecovery: round(average(recoveryValues), 0),
    avgSleepDuration: round(average(sleepDurationValues), 1),
    avgSleepEfficiency: round(average(sleepEfficiencyValues), 0),
    avgRestingHr: round(average(restingHrValues), 0),
    lowRecoveryDays14,
    poorSleepDays14,
    elevatedRestingHrDays14,
    totalObservations: observations.length,
    recentObservationCount: recent.length,
  };

  const severeSleepPattern = (stats.avgSleepDuration ?? 10) < 5.8 || (stats.avgSleepEfficiency ?? 100) < 80 || poorSleepDays14 >= 7;
  const moderateSleepPattern = severeSleepPattern || (stats.avgSleepDuration ?? 10) < 6.5 || (stats.avgSleepEfficiency ?? 100) < 85 || poorSleepDays14 >= 3;
  const severeRecoveryPattern = (stats.avgRecovery ?? 100) < 50 || lowRecoveryDays14 >= 7;
  const moderateRecoveryPattern = severeRecoveryPattern || (stats.avgRecovery ?? 100) < 60 || lowRecoveryDays14 >= 4;
  const severeRestingHrPattern = (stats.avgRestingHr ?? 0) >= 68 || elevatedRestingHrDays14 >= 7;
  const moderateRestingHrPattern = severeRestingHrPattern || (stats.avgRestingHr ?? 0) >= 62 || elevatedRestingHrDays14 >= 4;

  let patientInterpretation: PatientInterpretationCard;
  if (severeSleepPattern || severeRecoveryPattern || severeRestingHrPattern) {
    patientInterpretation = {
      tone: 'action',
      title: 'A few wearable patterns look persistent enough to act on.',
      summary: 'Your recent wearable data suggests sustained sleep strain, low recovery, or elevated resting load rather than a one-off off-week.',
      nextSteps: [
        'Start with a two-week sleep reset: steady wake time, earlier wind-down, and less late caffeine or alcohol.',
        'If poor sleep keeps showing up, consider CBT-I or a sleep therapist rather than self-experimenting indefinitely.',
        'If you notice loud snoring, witnessed apnoea, daytime sleepiness, palpitations, or illness symptoms, discuss formal review rather than relying on wearable data alone.',
      ],
      caution: 'Wearable data can highlight patterns, but it does not diagnose a sleep disorder, overtraining syndrome, or a cardiac issue on its own.',
    };
  } else if (moderateSleepPattern || moderateRecoveryPattern || moderateRestingHrPattern) {
    patientInterpretation = {
      tone: 'watch',
      title: 'There are a few recovery and sleep signals worth tightening up.',
      summary: 'Melio is seeing enough sleep, recovery, or resting-heart-rate drift to justify a structured optimisation pass before calling this settled.',
      nextSteps: [
        'Keep wake time, sleep opportunity, and training load more consistent for the next two weeks.',
        'Review late meals, alcohol, travel, and stress load if recovery remains stubbornly low.',
        'If the pattern persists after a couple of weeks, raise it with Melio so we can decide whether therapist or clinician support makes sense.',
      ],
      caution: 'This is a prevention and pattern-recognition layer, not a diagnosis or treatment instruction.',
    };
  } else {
    patientInterpretation = {
      tone: 'stable',
      title: 'Your current wearable pattern looks fairly stable.',
      summary: 'Melio is not seeing an obvious short-term recovery or sleep issue that needs urgent follow-up from the recent wearable window.',
      nextSteps: [
        'Keep sleep and recovery routines steady rather than making abrupt changes off a single metric.',
        'Use future uploads to track whether this remains stable over time.',
      ],
      caution: 'A stable wearable pattern still does not rule out symptoms, stress, or clinical issues that deserve attention.',
    };
  }

  const reviewPrompts: OpsReviewPromptSuggestion[] = [];
  if (moderateSleepPattern) {
    reviewPrompts.push({
      key: 'sleep_pattern_review',
      title: 'Review persistent sleep quality pattern',
      detail: `Recent wearable window suggests poor sleep on ${poorSleepDays14} recent signal days with average sleep ${stats.avgSleepDuration ?? '—'}h and sleep efficiency ${stats.avgSleepEfficiency ?? '—'}%. Consider sleep optimisation support first, then CBT-I / sleep therapist referral if persistent.`,
      severity: severeSleepPattern ? 'urgent' : 'priority',
      shouldEscalate: severeSleepPattern,
      escalationSummary: severeSleepPattern ? 'Persistent poor sleep signal from wearable data may warrant clinician review or formal sleep assessment screening.' : undefined,
    });
  }
  if (moderateRecoveryPattern) {
    reviewPrompts.push({
      key: 'recovery_pattern_review',
      title: 'Review repeated low recovery cluster',
      detail: `Average recovery is ${stats.avgRecovery ?? '—'}% with ${lowRecoveryDays14} low-recovery days in the recent window. Review training load, illness, travel, alcohol, and broader stress context before offering any intervention.`,
      severity: severeRecoveryPattern ? 'priority' : 'routine',
      shouldEscalate: severeRecoveryPattern,
      escalationSummary: severeRecoveryPattern ? 'Recovery pattern is persistently suppressed and may justify clinician review if paired with symptoms or other concerning context.' : undefined,
    });
  }
  if (moderateRestingHrPattern) {
    reviewPrompts.push({
      key: 'resting_hr_review',
      title: 'Review elevated resting heart-rate trend',
      detail: `Average resting heart rate is ${stats.avgRestingHr ?? '—'} bpm with ${elevatedRestingHrDays14} elevated days in the recent window. Check for deconditioning, illness, poor sleep, stimulant load, and stress before deciding on next steps.`,
      severity: severeRestingHrPattern ? 'urgent' : 'priority',
      shouldEscalate: severeRestingHrPattern,
      escalationSummary: severeRestingHrPattern ? 'Resting heart-rate trend may merit clinician review if persistent or paired with palpitations, chest symptoms, infection concerns, or exercise intolerance.' : undefined,
    });
  }

  const escalationLadder: EscalationLadderItem[] = [
    {
      key: 'sleep',
      label: 'Sleep',
      level: severeSleepPattern ? 'escalate' : moderateSleepPattern ? 'support' : 'monitor',
      detail: severeSleepPattern
        ? 'Move beyond self-directed advice if this persists: consider CBT-I / sleep therapist support and screen for red flags.'
        : moderateSleepPattern
          ? 'Start with routine optimisation, then escalate if the next 2-4 weeks do not improve.'
          : 'Monitor and reinforce stable routines.',
    },
    {
      key: 'recovery',
      label: 'Recovery',
      level: severeRecoveryPattern ? 'support' : moderateRecoveryPattern ? 'support' : 'monitor',
      detail: severeRecoveryPattern
        ? 'Review stress, illness, and load promptly rather than relying on supplements or speculation.'
        : moderateRecoveryPattern
          ? 'Use coaching-style behaviour changes first: easier recovery days, hydration, sleep, and load review.'
          : 'No obvious recovery escalation from the recent window.',
    },
    {
      key: 'resting_hr',
      label: 'Resting heart rate',
      level: severeRestingHrPattern ? 'escalate' : moderateRestingHrPattern ? 'support' : 'monitor',
      detail: severeRestingHrPattern
        ? 'Escalate if persistent or paired with symptoms such as palpitations, chest discomfort, infection, or exercise intolerance.'
        : moderateRestingHrPattern
          ? 'Recheck against sleep, recovery, alcohol, illness, travel, and stimulant context before escalating.'
          : 'Current resting-heart-rate pattern does not force action.',
    },
  ];

  return {
    stats,
    patientInterpretation,
    reviewPrompts,
    escalationLadder,
  };
}
